/**
 * Merchant list + dashboard hooks.
 *
 * All merchant-scoped dashboard endpoints (`/merchants/{id}/dashboard/*`)
 * are not yet typed in the OpenAPI build — handled with `as never` casts.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';
import { merchantOpsKeys, unwrap } from './keys';

export type MerchantResponse = components['schemas']['MerchantResponse'];
export type MerchantPage = components['schemas']['PageResponseMerchantResponse'];

/**
 * A merchant the current user belongs to (`GET /api/v1/merchants/me`,
 * `MerchantMembershipResponse`). `id` is the merchant id. Used to scope the
 * catalog editor for a logged-in *merchant* (admins use {@link useMerchantsList}).
 */
export interface MerchantMembership {
  id?: string;
  name?: string;
  logoUrl?: string;
  status?: string;
  memberRole?: string;
  isAcceptingOrders?: boolean;
  isOpen?: boolean;
}

/**
 * Merchants the current user is a member of. Enabled only for merchant users
 * (admins would 200 with their memberships too, but they pick from the full
 * `/admin/merchants` list instead, so we skip the call for them).
 */
export function useMyMerchants(enabled: boolean): UseQueryResult<MerchantMembership[], Error> {
  return useQuery<MerchantMembership[], Error>({
    queryKey: [...merchantOpsKeys.all, 'my-merchants'],
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/merchants/me' as never, {} as never);
      return unwrap(data as MerchantMembership[] | undefined, error);
    },
  });
}

export function useMerchantsList(): UseQueryResult<MerchantPage, Error> {
  return useQuery<MerchantPage, Error>({
    queryKey: merchantOpsKeys.merchants(),
    queryFn: async () => {
      // GET /admin/merchants is not yet in the generated OpenAPI (only POST
      // is typed). Use `as never` cast — same pattern as `categories/hooks`.
      const { data, error } = await api.GET(
        '/api/v1/admin/merchants' as never,
        { params: { query: { page: 0, size: 100 } } } as never,
      );
      return unwrap(data as MerchantPage | undefined, error);
    },
  });
}

export interface MerchantDashboardStats {
  ordersToday?: number;
  revenueToday?: number;
  acceptedOrdersToday?: number;
  pendingOrders?: number;
  averageRating?: number;
  totalProducts?: number;
}

export function useMerchantStats(
  merchantId: string | null,
  date: string | null,
): UseQueryResult<MerchantDashboardStats, Error> {
  return useQuery<MerchantDashboardStats, Error>({
    queryKey: merchantOpsKeys.dashboard(merchantId ?? 'none', date),
    enabled: merchantId !== null,
    queryFn: async () => {
      if (!merchantId) throw new Error('Selecione um estabelecimento');
      const query: Record<string, string> = {};
      if (date) query.date = date;
      const { data, error } = await api.GET(
        '/api/v1/merchants/{id}/dashboard/stats' as never,
        {
          params: { path: { id: merchantId }, query },
        } as never,
      );
      return unwrap(data as MerchantDashboardStats | undefined, error);
    },
  });
}

export interface MerchantRecentOrder {
  id?: string;
  customerName?: string;
  total?: number;
  status?: string;
  createdAt?: string;
}

export function useMerchantRecentOrders(
  merchantId: string | null,
  limit = 10,
): UseQueryResult<MerchantRecentOrder[], Error> {
  return useQuery<MerchantRecentOrder[], Error>({
    queryKey: merchantOpsKeys.recentOrders(merchantId ?? 'none', limit),
    enabled: merchantId !== null,
    queryFn: async () => {
      if (!merchantId) throw new Error('Selecione um estabelecimento');
      const { data, error } = await api.GET(
        '/api/v1/merchants/{id}/dashboard/recent-orders' as never,
        {
          params: { path: { id: merchantId }, query: { limit } },
        } as never,
      );
      return unwrap(data as MerchantRecentOrder[] | undefined, error);
    },
  });
}
